import { useCallback, useContext, useRef, useState } from 'react'
import { exportMicroLearning } from '../../../api/api'
import { MicroLearningExportFormat } from '../../../api/models'
import { AppStateContext } from '../../../state/AppProvider'
import { downloadBlob, extractFilename } from '../../../utils/download'

export const useExport = (mlId: string) => {
  const [isExporting, setIsExporting] = useState(false)
  const isExportingRef = useRef(false)
  const appStateContext = useContext(AppStateContext)
  const dispatchRef = useRef(appStateContext?.dispatch)
  dispatchRef.current = appStateContext?.dispatch

  const runExport = useCallback(
    async function runExport(targetMlId: string, format: MicroLearningExportFormat = 'xlsx') {
      if (isExportingRef.current) return
      isExportingRef.current = true
      setIsExporting(true)

      const notifyExportError = () =>
        dispatchRef.current?.({
          type: 'SET_SNACKBAR_STATE',
          payload: {
            message: 'Export failed. Please try again.',
            severity: 'error',
            action: { label: 'Retry', onClick: () => runExport(targetMlId, format) },
          },
        })

      try {
        const response = await exportMicroLearning(targetMlId, format)

        if (response.ok) {
          const blob = await response.blob()
          if (blob.size === 0) {
            notifyExportError()
            return
          }
          const ext = format === 'pptx' ? 'pptx' : 'xlsx'
          const fallbackName = `${targetMlId}_Micro-Learning_Export.${ext}`
          const filename = extractFilename(response, fallbackName)
          downloadBlob(blob, filename)
          dispatchRef.current?.({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Export downloaded successfully.', severity: 'success' },
          })
        } else {
          notifyExportError()
        }
      } catch {
        notifyExportError()
      } finally {
        isExportingRef.current = false
        setIsExporting(false)
      }
    },
    [],
  )

  const triggerExport = useCallback(
    async (format: MicroLearningExportFormat = 'xlsx') => {
      await runExport(mlId, format)
    },
    [mlId, runExport],
  )

  return { isExporting, triggerExport }
}
